import type { Chosen } from './print/looks'
import { keepPreviousData, useInfiniteQuery, useQuery, type QueryClient } from '@tanstack/react-query'
import { showDate } from '../../shared/tree'
import { api, ApiError } from './api'
import type { Space } from './families'
import { placesSaid, type Place } from './spaces'
import type { Crop } from '../../shared/crop'
import { DISPLAY, MEDIUM, refUrl, setCrop, THUMB, type PreparedPhoto } from './photos'

export type AlbumSummary = {
  id: string
  title: string
  collectionId: string | null
  photos: number
  first: string | null
  last: string | null
  // Shared in from another circle: seen here, added to there. Missing from an older server.
  elsewhere?: boolean
  // From another circle, and here only because it's shared with Everyone. Missing from an older server.
  forEveryone?: boolean
  mine: boolean
  // Its keeper's name; missing from an older server.
  by?: string
  cover: string | null
  covers: string[]
  // A few of its photos, cover first, to swipe through on its card; missing from an older server.
  preview?: string[]
}
export type Collection = { id: string; title: string; mine: boolean }
// The photos in no album: how many you can see, how many of them you added, and the newest few of each.
export type LoosePhotos = { photos: number; mine: number; covers: string[]; mineCovers: string[] }
export type Albums = {
  albums: AlbumSummary[]
  collections: Collection[]
  // Missing from an app that loaded before photos could be in no album.
  loose?: LoosePhotos
  tags: { tag: string; photos: number }[]
  // covers: their newest few photos, for their card on the People tab; missing from an app that loaded before it.
  people: { id: string; name: string; living: boolean; portrait: string | null; photos: number; covers?: string[] }[]
  years: { year: string; photos: number }[]
}
export type AlbumPhoto = {
  id: string
  ref: string
  width: number
  height: number
  // The whole photo and the part of it that shows, for printing from the original; missing from an app that loaded
  // before printed storybooks.
  original?: { width: number; height: number }
  crop?: Crop | null
  caption: string
  takenOn: string | null
  canEdit: boolean
  // living is missing from an album loaded before the storybook; only false means in memory.
  people: { id: string; name: string; living?: boolean; portrait: string | null }[]
  tags: string[]
}
export type Album = {
  id: string
  familyId: string
  title: string
  description: string
  // The keeper's name; missing from an app that loaded before the storybook.
  by?: string
  space: Space | null
  // Who it's for: Everyone, or the places it's shared with that you're in (neither: only its keeper). Missing from an
  // app that loaded before albums could be shared with more than one place.
  everyone?: boolean
  sharedWith?: Place[]
  collection: { id: string; title: string } | null
  mine: boolean
  // Whether you can add photos: only people in its own circle can. Missing from an older app.
  canAdd?: boolean
  coverPhotoId: string | null
  // Its keeper's design for the printed Storybook version; missing from an older server.
  printDesign?: Chosen | null
  photos: AlbumPhoto[]
}
export type Photo = {
  id: string
  ref: string
  familyId: string
  // As it shows, cropped; original is the whole photo, crop the part kept (null for all of it).
  width: number
  height: number
  original: { width: number; height: number }
  crop: Crop | null
  // Its own title, beside its caption (its description); empty when it has none. Missing from an app that loaded
  // before titles.
  title?: string
  caption: string
  takenOn: string | null
  createdAt: number
  by: string
  // Null when it's in no album (or in one you can't see).
  album: { id: string; title: string } | null
  // Every place it's shared with on its own that you're in, in any of your circles (empty: only whoever added it); the
  // first of those in the circle it was added in; and who sees it through its album.
  sharedWith: Place[]
  // Shared with Everyone on its own. Missing from an app that loaded before Everyone.
  everyone?: boolean
  space: Space | null
  albumSpace: Space | null
  // Who sees its album, when it's in one you see.
  albumShared?: { everyone: boolean; sharedWith: Place[] } | null
  // Whether you added it: only you can change who sees it.
  mine: boolean
  canEdit: boolean
  albumMine: boolean
  cover: boolean
  people: { id: string; name: string; living: boolean; portrait: string | null }[]
  tags: string[]
  position: number
  of: number
  previous: string | null
  next: string | null
  around: string[]
}
export type FoundPhoto = Pick<Photo, 'id' | 'ref' | 'width' | 'height' | 'caption' | 'takenOn' | 'album' | 'canEdit'>
// One way into the photos across albums, or the photos in none.
export type Browse = { tag: string } | { person: string } | { year: string } | { loose: '1' }

// A family's albums and the ways into them, all you can see or only those in one space.
export const useAlbums = (familyId: string, spaceId?: string) =>
  useQuery({
    queryKey: ['albums', familyId, spaceId ?? 'all'],
    queryFn: () => api<Albums>(`/families/${familyId}/albums${spaceId ? `?space=${encodeURIComponent(spaceId)}` : ''}`),
  })

export const useAlbum = (id: string) =>
  useQuery({ queryKey: ['album', id], queryFn: () => api<Album>(`/albums/${encodeURIComponent(id)}`) })

// One photo with what's known about it, to open, or to fetch ahead (the ones either side, the one being tapped).
export const photoQuery = (id: string) => ({
  queryKey: ['photo', id],
  queryFn: () => api<Photo>(`/album-photos/${encodeURIComponent(id)}`),
})

// Once a photo is turned or cropped its copies have a new address, and it shows on pages all over (albums, the
// stream, Home, people, covers, books), so everything is asked again rather than chasing each list it's in.
export const afterPhotoEdit = (queryClient: QueryClient) => queryClient.invalidateQueries()

export const usePhoto = (id: string) =>
  useQuery({
    ...photoQuery(id),
    // Stepping to the next photo keeps this one up until the next has arrived, as in any photo viewer.
    placeholderData: keepPreviousData,
  })

export const useFoundPhotos = (familyId: string, by: Browse | null, spaceId?: string, mine = false) =>
  useQuery({
    queryKey: ['found-photos', familyId, by, spaceId ?? 'all', mine],
    enabled: !!by,
    queryFn: () => {
      const query = new URLSearchParams(by as Record<string, string>)
      if (spaceId) query.set('space', spaceId)
      if (mine) query.set('mine', '1')
      return api<{ photos: FoundPhoto[] }>(`/families/${familyId}/photos?${query}`)
    },
  })

// The stream: every photo, as one run newest-added first ("added") or grouped one way. A group's key is "" for the photos that have no year, no home, or no one
// tagged; its label then says so.
export type StreamBy = 'added' | 'year' | 'family' | 'person'
export type StreamGroup = { key: string; label: string; photos: number; portrait?: string | null; living?: boolean }
export type StreamPhoto = Pick<
  Photo,
  'id' | 'ref' | 'width' | 'height' | 'caption' | 'takenOn' | 'album' | 'canEdit'
> & {
  group: string
}
export type StreamPage = { groups: StreamGroup[]; photos: StreamPhoto[]; next: number | null }

export const useStream = (familyId: string, by: StreamBy, mine: boolean, spaceId?: string) =>
  useInfiniteQuery({
    queryKey: ['stream', familyId, by, mine, spaceId ?? 'all'],
    initialPageParam: 0,
    getNextPageParam: (last: StreamPage) => last.next,
    queryFn: ({ pageParam }) => {
      const query = new URLSearchParams({ by, page: String(pageParam) })
      if (mine) query.set('mine', '1')
      if (spaceId) query.set('space', spaceId)
      return api<StreamPage>(`/families/${familyId}/stream?${query}`)
    },
  })

// The loaded pages as groups, in the order the server sent them. A photo shows once in a group even if it straddled
// two pages while someone was tagging.
export function streamGroups(pages: StreamPage[]) {
  const sizes = new Map((pages.at(-1)?.groups ?? []).map((g) => [g.key, g]))
  const groups = new Map<string, { group: StreamGroup; photos: StreamPhoto[]; seen: Set<string> }>()
  for (const photo of pages.flatMap((p) => p.photos)) {
    let entry = groups.get(photo.group)
    if (!entry) {
      const group = sizes.get(photo.group) ?? { key: photo.group, label: '', photos: 0 }
      entry = { group, photos: [], seen: new Set() }
      groups.set(photo.group, entry)
    }
    if (entry.seen.has(photo.id)) continue
    entry.seen.add(photo.id)
    entry.photos.push(photo)
  }
  return [...groups.values()].map(({ group, photos }) => ({ group, photos }))
}

export const albumPhotoUrl = (ref: string, variant: 'original' | 'display' | 'medium' | 'thumb') =>
  refUrl('album-photos', ref, variant)

const LONG_EDGE = { thumb: THUMB, medium: MEDIUM, display: DISPLAY }

// The copies of a photo a browser can choose between, each with its real width, so a phone fetches the smallest
// one that stays sharp on its screen and a big screen the largest. A copy is never bigger than the photo itself.
export function photoSrcSet(
  photo: { ref: string; width: number; height: number },
  variants: (keyof typeof LONG_EDGE)[],
  url: (ref: string, variant: keyof typeof LONG_EDGE) => string = albumPhotoUrl,
) {
  const widths = new Map<number, string>()
  for (const variant of variants) {
    const scale = Math.min(1, LONG_EDGE[variant] / Math.max(photo.width, photo.height))
    const width = Math.max(1, Math.round(photo.width * scale))
    // A small photo's copies come out the same size; the smallest of them is enough.
    if (!widths.has(width)) widths.set(width, url(photo.ref, variant))
  }
  return [...widths].map(([width, url]) => `${url} ${width}w`).join(', ')
}

// Where new photos go: an album, or no album, kept for whoever adds them (spaceId null) or shared with a space.
// A photo added to no album says where it's shared: spaces in any of your circles, none for only you. One added from
// someone's page also says who's in it.
export type PhotoTarget =
  { albumId: string } | { familyId: string; spaceIds: string[]; everyone?: boolean; people?: string[] }

// onProgress hears how many bytes of the upload have gone so far, which fetch can't tell, hence the XMLHttpRequest.
export async function uploadAlbumPhoto(
  to: PhotoTarget,
  photo: PreparedPhoto,
  onProgress?: (sent: number, total: number) => void,
) {
  const form = new FormData()
  form.set('original', photo.original)
  form.set('display', photo.display, 'display')
  if (photo.medium) form.set('medium', photo.medium, 'medium')
  form.set('thumb', photo.thumb, 'thumb')
  form.set('width', String(photo.width))
  form.set('height', String(photo.height))
  setCrop(form, photo)
  if ('familyId' in to) {
    form.set('spaceIds', to.spaceIds.join(','))
    if (to.everyone) form.set('everyone', '1')
  }
  if ('familyId' in to && to.people?.length) form.set('people', to.people.join(','))
  const url =
    'albumId' in to
      ? `/api/albums/${encodeURIComponent(to.albumId)}/photos`
      : `/api/families/${encodeURIComponent(to.familyId)}/photos`
  const { status, body } = await post(url, form, onProgress)
  const data = (body ?? {}) as { id?: string; error?: string }
  if (status < 200 || status >= 300 || !data.id) {
    throw new ApiError(status, data.error ?? 'The photo didn’t upload. Please try again.')
  }
  return data.id
}

function post(url: string, form: FormData, onProgress?: (sent: number, total: number) => void) {
  return new Promise<{ status: number; body: unknown }>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    xhr.withCredentials = true
    xhr.responseType = 'json'
    if (onProgress) xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded, e.total)
    xhr.onload = () => resolve({ status: xhr.status, body: xhr.response as unknown })
    xhr.onerror = () => reject(new Error('The photo didn’t upload. Check your connection and try again.'))
    xhr.onabort = xhr.onerror
    xhr.send(form)
  })
}

export type Organize = {
  takenOn?: string | null
  // An album to send them to; null takes them out of their album.
  albumId?: string | null
  addPeople?: string[]
  removePeople?: string[]
  addTags?: string[]
  removeTags?: string[]
}

// The server takes this many photos per change; a bigger selection goes in turns.
const BATCH = 50

export async function organize(familyId: string, photos: string[], change: Organize) {
  for (let i = 0; i < photos.length; i += BATCH) {
    await api(`/families/${familyId}/photos/organize`, {
      method: 'POST',
      body: { photos: photos.slice(i, i + BATCH), ...change },
    })
  }
}

// Tags as typed: "lake house, Christmas morning" is two. The server keeps them lowercase.
export const splitTags = (text: string) =>
  text
    .split(',')
    .map((x) => x.trim().replace(/^#+/, '').replace(/\s+/g, ' ').toLowerCase())
    .filter(Boolean)

// When an album's photos were taken, as plainly as the dates allow: "1987", "1986 to 1989".
export function span(first: string | null, last: string | null) {
  if (!first || !last) return null
  const [a, b] = [first.slice(0, 4), last.slice(0, 4)]
  return a === b ? a : `${a} to ${b}`
}

// When an album's photos were taken, to the day where the dates allow: "12 Jun 1987 – 28 Nov 1987".
export function dates(first: string | null, last: string | null) {
  if (!first || !last) return null
  return first === last ? showDate(first) : `${showDate(first)} – ${showDate(last)}`
}

// Everyone tagged anywhere in an album, most-photographed first.
export function peopleIn(photos: Pick<AlbumPhoto, 'people'>[]) {
  const seen = new Map<string, AlbumPhoto['people'][number] & { photos: number }>()
  for (const p of photos.flatMap((x) => x.people)) {
    const known = seen.get(p.id)
    seen.set(p.id, { ...p, photos: (known?.photos ?? 0) + 1 })
  }
  return [...seen.values()].sort((a, b) => b.photos - a.photos || a.name.localeCompare(b.name))
}

// What a photo is called where it has no words of its own: its caption, else when it was taken, else its place.
export const photoName = (photo: { caption: string; takenOn: string | null }, n: number) =>
  photo.caption || (photo.takenOn ? `Photo from ${showDate(photo.takenOn)}` : `Photo ${n}`)

// The day a photo was added, on this device's calendar, as dates show everywhere else ("12 Jun 1987").
export function addedOn(at: number) {
  const d = new Date(at)
  const pad = (n: number) => String(n).padStart(2, '0')
  return showDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`)
}

// Who sees a photo, said plainly: its album's people and every place it's shared on its own that you know of. Everyone
// in a circle covers that circle's homes; circles are named once there's more than one. `circle` is the name of the
// circle it was added in, where its album is.
export function whoSees(
  photo: Pick<Photo, 'albumSpace' | 'albumShared' | 'sharedWith' | 'everyone' | 'familyId' | 'mine' | 'by'>,
  circle: string,
) {
  if (photo.everyone || photo.albumShared?.everyone) return 'Everyone'
  const places: Place[] = [
    ...(photo.albumShared
      ? photo.albumShared.sharedWith
      : photo.albumSpace
        ? [{ ...photo.albumSpace, familyId: photo.familyId, circle }]
        : []),
    ...(photo.sharedWith ?? []),
  ]
  if (places.length) return placesSaid(places, new Set(places.map((x) => x.familyId)).size > 1)
  return photo.mine ? 'Only you' : `Only ${photo.by}`
}
